import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { UserDTO } from '../models/users.dto';
import { IdentityFacade } from './identity.facade';
import { UserLogin } from '../models/user-login.interface';
import { clearStoredToken, storeToken } from '@domains/shared/domain/utils/auth-storage';
import { isJwtExpired, storedToken } from '@domains/shared/domain/utils/jwt';
import { AuthService } from '../infrastructure/system/auth.service';
import { BackofficeCompanyService } from '../infrastructure/backoffice/backoffice-company.service';
import { BackofficeCompanyDetailDTO } from '../models/company-backoffice-detail.interface';
import { UserRoleEnum } from '../models/enums/user-role.enum';
import { TokenPurposeEnum } from '../models/enums/token-purpose.enum';

// Account Facade was renamed into AuthFacade
@Injectable({ providedIn: 'root' })
export class AuthFacade {
  constructor(
    private identityFacade: IdentityFacade,
    private authService: AuthService,
    private companyService: BackofficeCompanyService

  ) { }

  changeCompany(companyId: string): Observable<BackofficeCompanyDetailDTO | undefined> {
    return this.authService.changeCompany(companyId).pipe(
      tap((user) => {
        storeToken(user.token);

      }),
      switchMap((user) => {
        // ⚠ Detail firmy je pod politikou `BackofficeSection`, tedy jen role Admin.
        // Student ve víc firmách tu dostával 403: přepnutí samo proběhlo, ale
        // error-interceptor ohlásil chybu a v konzoli zůstala 403 — pro člověka to
        // vypadalo, že se přepnutí nepovedlo. Táž past jako EL-2089 #5, tady zbyla
        // v přepínání firem (EL-2178).
        //
        // Role v odpovědi jsou role v NOVÉ firmě (backend je přepočítá), takže se
        // rozhoduje podle cílové firmy, ne podle té, ze které se odchází.
        if (!this.canReadCompanyDetail(user)) {
          this.identityFacade.setCurrentUser(user);

          return of(undefined);
        }

        return this.setCurrentCompany(companyId).pipe(
          (tap(() => this.identityFacade.setCurrentUser(user)))
        );
      })
    );
  }

  /** Backoffice detail firmy si smí načíst jen správce — student na něj dostane 403. */
  private canReadCompanyDetail(user: UserDTO): boolean {
    return (user?.assignedRoles ?? []).some((role) =>
      role.name === UserRoleEnum.CompanyAdmin || role.name === UserRoleEnum.SuperAdmin);
  }

  /** Jméno, které jde na certifikát. Po uložení se obnoví identita, ať se změna projeví všude. */
  changeProfile(fullName: string): Observable<UserDTO> {
    return this.authService.changeProfile(fullName).pipe(
      tap((user) => this.identityFacade.setCurrentUser(user))
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.authService.changePassword(currentPassword, newPassword);
  }

  changeNotifications(enabled: boolean): Observable<UserDTO> {
    return this.authService.changeNotifications(enabled).pipe(
      tap((user) => this.identityFacade.setCurrentUser(user))
    );
  }

  changeCourseNotifications(studentId: string, enabled: boolean): Observable<void> {
    return this.authService.changeCourseNotifications(studentId, enabled);
  }

  changeCourseArchive(studentId: string, archived: boolean): Observable<void> {
    return this.authService.changeCourseArchive(studentId, archived);
  }

  unsubscribePreview(token: string): Observable<{ courseName: string }> {
    return this.authService.unsubscribePreview(token);
  }

  unsubscribe(token: string): Observable<{ courseName: string }> {
    return this.authService.unsubscribe(token);
  }

  /**
   * Uloží jazyk zvolený vlaječkou do profilu uživatele. Server hodnotu ořízne na jazyky
   * firmy, proto se do identity vrací to, co opravdu uložil — ne to, co jsme poslali.
   */
  changeTheme(theme: string): Observable<UserDTO> {
    return this.authService.changeTheme(theme).pipe(
      tap((user) => this.identityFacade.setCurrentUser(user))
    );
  }

  /** Preferovaný počet řádků v tabulkách. Ukládá se na účet, ať přejde i na jiné zařízení. */
  changePageSize(pageSize: number): Observable<UserDTO> {
    return this.authService.changePageSize(pageSize).pipe(
      tap((user) => this.identityFacade.setCurrentUser(user))
    );
  }

  changeLanguage(languageIso: string): Observable<UserDTO> {
    return this.authService.changeLanguage(languageIso).pipe(
      tap((user) => this.identityFacade.setCurrentUser(user))
    );
  }

  /**
   * Detail firmy do identity po přepnutí firmy.
   *
   * ⚠ Endpoint je pod politikou `BackofficeSection`, tedy jen role **Admin**. Student ve
   * víc firmách (a stejně tak SuperAdmin bez claimu k té firmě) dostane 403 — a bez
   * záchytu se z rozcestníku do firmy vůbec nepřepnul, přestože přepnutí samo proběhlo
   * (token už je nový). Detail firmy je doplněk, ne podmínka (EL-2089 #5).
   */
  private setCurrentCompany(companyId: string): Observable<BackofficeCompanyDetailDTO | undefined> {
    return this.companyService.getCompany(companyId).pipe(
      tap((company) => {
        this.identityFacade.setCurrentCompany(company)
      }),
      catchError(() => of(undefined)),
    );
  }
  loadUserIntoIdentity(): Observable<UserDTO | undefined> {
    return this.authService.getUserInfo().pipe(
      tap((user) => {
        this.identityFacade.setCurrentUser(user);
      })
    );
  }

  loginUser(data: UserLogin): Observable<UserDTO | undefined> {
    return this.authService.loginUser(data).pipe(
      switchMap((user) => {
        if(user) {
          storeToken(user.token);
        }
        return this.loadUserIntoIdentity();
      }),
    );
  }

  /**
   * Odhlásí uživatele na serveru i v prohlížeči — jen na tomto zařízení (EL-2343).
   *
   * Server zruší relaci tokenu; na jiných zařízeních zůstane uživatel přihlášený
   * (odhlášení všude je `logoutEverywhereUser`). ⚠ Selhání toho volání (výpadek sítě, token už stejně neplatný)
   * **nesmí** odhlášení zablokovat — token se z prohlížeče smaže tak jako tak.
   *
   * Identita v paměti se tu schválně nepřepisuje. Dřív se o to pokoušel
   * `loadUserIntoIdentity()` ve `finalize` — bez odběru, takže nedělal nic. Kdyby to
   * doopravdy proběhlo, dostal by ještě živý layout nepřihlášeného uživatele a zkusil
   * pro něj načíst logo firmy s nulovým id: 401 a hláška „přihlášení vypršelo".
   * Dovnitř aplikace se bez tokenu stejně nikdo nedostane (hlídá `AuthGuard`) a
   * identitu přepíše další přihlášení.
   */
  logoutUser(): Observable<undefined> {
    return this.logoutWith(() => this.authService.logout());
  }

  /**
   * Odhlásí uživatele na všech zařízeních (EL-2343) — server přetočí otisk účtu.
   * Běžné odhlášení (`logoutUser`) od EL-2343 ruší jen relaci tohoto zařízení.
   */
  logoutEverywhereUser(): Observable<undefined> {
    return this.logoutWith(() => this.authService.logoutEverywhere());
  }

  /** Počet jiných zařízení, kde je uživatel ještě přihlášený (EL-2343). */
  otherActiveSessions(): Observable<number> {
    return this.authService.otherSessions();
  }

  private logoutWith(serverCall: () => Observable<void>): Observable<undefined> {
    // Vypršelý token by server odmítl 401 a error interceptor by k „odhlášeno" přidal
    // „přihlášení vypršelo". Takový token už stejně nic neotevře.
    const token = storedToken();
    const serverLogout = token && !isJwtExpired(token) ? serverCall() : of(undefined);

    return serverLogout.pipe(
      catchError(() => of(undefined)),
      map(() => {
        clearStoredToken();

        return undefined;
      }),
    );
  }

  resetPassword(data: any): Observable<any> {
    return this.authService.resetPassword(data);
  }

  newPassword(data: any): Observable<any> {
    return this.authService.newPassword(data);
  }

  checkToken(token: string, purpose: TokenPurposeEnum): Observable<void> {
    return this.authService.checkToken(token, purpose);
  }

  activateAccount(data: any): Observable<any> {
    return this.authService.activateAccount(data);
  }
}
